<template>
  <div class="ai-shell" :class="{ open: isOpen, expanded }">
    <Transition name="ai-pop" mode="out-in" @after-enter="onPanelShown">
      <button
        v-if="!isOpen"
        ref="fab"
        key="fab"
        class="ai-fab"
        type="button"
        aria-label="Ouvrir l'assistant IA"
        @click="open"
      >
        <img src="@/assets/images/iaLOGO.png" alt="" />
        <span v-if="pendingConfirmation" class="ai-fab-badge" aria-hidden="true"></span>
      </button>

      <section
        v-else
        key="panel"
        class="ai-panel text-start"
        role="dialog"
        aria-label="Assistant IA"
        @keydown.esc="close"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <header class="ai-header">
          <div class="ai-brand">
            <span class="ai-brand-mark"><img src="@/assets/images/iaLOGO.png" alt="" /></span>
            <div>
              <div class="ai-title">Assistant IA</div>
              <div class="ai-status" :class="{ busy: isLoading }">
                <span class="ai-status-dot"></span>
                {{ isLoading ? 'Rédaction de la réponse…' : 'En ligne · connecté à vos données' }}
              </div>
            </div>
          </div>
          <div class="ai-header-actions">
            <button v-if="hasConversation" class="ai-icon-btn" type="button" title="Nouvelle conversation" aria-label="Nouvelle conversation" @click="resetConversation">
              <i class="far fa-edit"></i>
            </button>
            <button class="ai-icon-btn ai-expand-btn" type="button" :title="expanded ? 'Réduire' : 'Agrandir'" :aria-label="expanded ? 'Réduire' : 'Agrandir'" @click="expanded = !expanded">
              <i :class="expanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'"></i>
            </button>
            <button class="ai-icon-btn" type="button" title="Fermer (Échap)" aria-label="Fermer" @click="close">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </header>

        <div ref="messages" class="ai-messages" aria-live="polite" @scroll="onScroll">
          <AssistantWelcome v-if="!hasConversation" @pick="send({ text: $event, files: [] })" />
          <AssistantMessage
            v-for="(message, index) in messages"
            :key="message.id"
            :message="message"
            :retryable="index === messages.length - 1 && !!lastPayload && !isLoading"
            @navigate="goToRoute"
            @retry="retry"
          />
          <div v-if="isLoading" class="ai-typing">
            <span class="ai-typing-avatar"><img src="@/assets/images/iaLOGO.png" alt="" /></span>
            <span class="ai-typing-bubble" aria-label="L'assistant réfléchit"><span></span><span></span><span></span></span>
          </div>
        </div>

        <button v-if="showJump" type="button" class="ai-jump" @click="scrollToBottom(true)">
          <i class="fas fa-arrow-down"></i> Nouveaux messages
        </button>

        <AssistantConfirm
          v-if="pendingConfirmation"
          :pending="pendingConfirmation"
          :busy="isLoading"
          @confirm="confirmTool"
          @cancel="cancelConfirmation"
        />

        <AssistantComposer
          ref="composer"
          :busy="isLoading"
          :suggestions="quickSuggestions"
          :placeholder="pendingConfirmation ? 'Ou précisez votre demande…' : undefined"
          @send="send"
        />

        <div v-if="dragging" class="ai-drop">
          <i class="fas fa-cloud-upload-alt"></i>
          <strong>Déposez vos fichiers ici</strong>
          <span>PDF, images, Excel, CSV ou texte</span>
        </div>
      </section>
    </Transition>
  </div>
</template>

<script>
import { sendAssistantMessage } from '@/services/assistant-api';
import { loadAssistantSession, saveAssistantSession } from '@/utils/assistant-session';
import AssistantMessage from '@/components/assistant/AssistantMessage.vue';
import AssistantWelcome, { SUGGESTION_GROUPS } from '@/components/assistant/AssistantWelcome.vue';
import AssistantConfirm from '@/components/assistant/AssistantConfirm.vue';
import AssistantComposer from '@/components/assistant/AssistantComposer.vue';
import { buildCards, buildAssistantText, errorMessage, isValidConfirmation } from '@/components/assistant/assistant-format';

const FILES_ONLY_MESSAGE = 'Analyse les fichiers joints et propose l’action métier adaptée.';
const NEAR_BOTTOM_PX = 80;

export default {
  name: 'AiAssistantWidget',
  components: { AssistantMessage, AssistantWelcome, AssistantConfirm, AssistantComposer },

  data() {
    const saved = loadAssistantSession() || {};
    const messages = Array.isArray(saved.messages) ? saved.messages : [];
    return {
      isOpen: !!saved.isOpen,
      expanded: !!saved.expanded,
      isLoading: false,
      conversationId: saved.conversationId || null,
      pendingConfirmation: saved.pendingConfirmation || null,
      messages,
      nextMessageId: messages.reduce((max, m) => Math.max(max, m.id || 0), 0) + 1,
      lastPayload: null,
      nearBottom: true,
      showJump: false,
      dragging: false,
      dragDepth: 0,
      quickSuggestions: SUGGESTION_GROUPS.flatMap((group) => group.questions),
    };
  },

  computed: {
    hasConversation() {
      return this.messages.length > 0;
    },
    sessionSnapshot() {
      return {
        isOpen: this.isOpen,
        expanded: this.expanded,
        conversationId: this.conversationId,
        pendingConfirmation: this.pendingConfirmation,
        messages: this.messages,
      };
    },
  },

  watch: {
    sessionSnapshot: {
      deep: true,
      handler(state) {
        saveAssistantSession(state);
      },
    },
  },

  mounted() {
    if (this.isOpen) this.onPanelShown();
  },

  methods: {
    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
      this.$nextTick(() => this.$refs.fab?.focus());
    },

    onPanelShown() {
      if (!this.isOpen) return;
      this.scrollToBottom(true);
      this.$refs.composer?.focus();
    },

    addMessage(role, text, extra = {}) {
      this.messages.push({ id: this.nextMessageId++, role, text, at: Date.now(), ...extra });
      this.$nextTick(() => this.scrollToBottom(role === 'user'));
    },

    send({ text, files }) {
      if (this.isLoading) return;
      const message = text || FILES_ONLY_MESSAGE;
      this.pendingConfirmation = null;
      this.addMessage('user', message, { attachments: files.map((file) => ({ name: file.name })) });
      this.callAssistant({ message, files });
    },

    retry() {
      if (!this.lastPayload || this.isLoading) return;
      this.messages.pop();
      this.callAssistant(this.lastPayload);
    },

    confirmTool() {
      if (!this.pendingConfirmation || this.isLoading) return;
      const pending = this.pendingConfirmation;
      this.pendingConfirmation = null;
      this.callAssistant({
        message: pending.message,
        confirmed: true,
        confirmedToolName: pending.toolName,
        confirmedArguments: pending.toolArguments,
      });
    },

    cancelConfirmation() {
      this.pendingConfirmation = null;
      this.addMessage('assistant', 'Action annulée. Rien n’a été modifié.');
    },

    async callAssistant(payload) {
      this.isLoading = true;
      this.lastPayload = null;
      this.$nextTick(() => this.scrollToBottom());
      try {
        const response = await sendAssistantMessage({ ...payload, conversationId: this.conversationId });
        if (response.conversationId) {
          this.conversationId = response.conversationId;
        }
        if (response.needsConfirmation) {
          const args = response.toolArguments || {};
          if (!isValidConfirmation(response.toolName, args)) {
            this.addMessage(
              'assistant',
              'Je n’ai pas assez d’informations fiables pour confirmer cette création. Veuillez renvoyer le fichier ou préciser le montant HT.'
            );
            return;
          }
          this.pendingConfirmation = { message: payload.message, toolName: response.toolName, toolArguments: args };
        }

        const cards = buildCards(response);
        this.addMessage('assistant', buildAssistantText(response, cards), { toolName: response.toolName, cards });
      } catch (error) {
        this.lastPayload = payload;
        this.addMessage('assistant', errorMessage(error), { error: true });
      } finally {
        this.isLoading = false;
      }
    },

    resetConversation() {
      this.conversationId = null;
      this.pendingConfirmation = null;
      this.lastPayload = null;
      this.messages = [];
      this.$refs.composer?.focus();
    },

    onScroll() {
      const el = this.$refs.messages;
      if (!el) return;
      this.nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      if (this.nearBottom) this.showJump = false;
    },

    scrollToBottom(force = false) {
      const el = this.$refs.messages;
      if (!el) return;
      if (force || this.nearBottom) {
        el.scrollTop = el.scrollHeight;
        this.nearBottom = true;
        this.showJump = false;
      } else {
        this.showJump = true;
      }
    },

    onDragEnter() {
      this.dragDepth += 1;
      this.dragging = true;
    },

    onDragLeave() {
      this.dragDepth = Math.max(this.dragDepth - 1, 0);
      if (!this.dragDepth) this.dragging = false;
    },

    onDrop(event) {
      this.dragDepth = 0;
      this.dragging = false;
      this.$refs.composer?.addFiles(event.dataTransfer?.files);
    },

    goToRoute(route) {
      if (!route) return;
      // On phones the panel covers the page: close it so the user sees where they landed.
      if (window.matchMedia('(max-width: 767px)').matches) this.isOpen = false;
      this.$router.push(route).catch(() => {});
    },
  },
};
</script>

<style scoped>
.ai-shell {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1400;
  --ai-ink: #0f172a;
  --ai-muted: #64748b;
  --ai-faint: #94a3b8;
  --ai-line: #e2e8f0;
  --ai-line-strong: #cbd5e1;
  --ai-surface: #ffffff;
  --ai-soft: #f8fafc;
  --ai-primary: #4f46e5;
  --ai-primary-soft: #eef2ff;
  --ai-primary-line: #c7d2fe;
  --ai-primary-ring: rgba(79, 70, 229, 0.15);
  --ai-ease: cubic-bezier(0.23, 1, 0.32, 1);
}

.ai-fab { position: relative; width: 58px; height: 58px; border: 1px solid var(--ai-line); border-radius: 50%; background: var(--ai-surface); box-shadow: 0 12px 32px rgba(79, 70, 229, 0.28); display: grid; place-items: center; cursor: pointer; transition: transform 180ms var(--ai-ease), box-shadow 180ms ease; }
.ai-fab:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(79, 70, 229, 0.34); }
.ai-fab:active { transform: scale(0.96); }
.ai-fab img { width: 38px; height: 38px; object-fit: contain; }
.ai-fab-badge { position: absolute; top: 2px; right: 2px; width: 14px; height: 14px; border: 2px solid #fff; border-radius: 50%; background: #f59e0b; }

.ai-panel { position: relative; width: min(410px, calc(100vw - 32px)); height: min(660px, calc(100vh - 48px)); display: flex; flex-direction: column; overflow: hidden; border: 1px solid var(--ai-line); border-radius: 18px; background: var(--ai-soft); box-shadow: 0 24px 64px rgba(15, 23, 42, 0.22); transform-origin: bottom right; transition: width 220ms var(--ai-ease), height 220ms var(--ai-ease); }
.ai-shell.expanded .ai-panel { width: min(640px, calc(100vw - 48px)); height: calc(100vh - 48px); }

.ai-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 12px 12px 16px; border-bottom: 1px solid var(--ai-line); background: var(--ai-surface); }
.ai-brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ai-brand-mark { flex: 0 0 38px; height: 38px; border-radius: 12px; background: var(--ai-primary-soft); display: grid; place-items: center; }
.ai-brand-mark img { width: 26px; height: 26px; object-fit: contain; }
.ai-title { font-size: 15px; font-weight: 700; color: var(--ai-ink); }
.ai-status { display: flex; align-items: center; gap: 6px; margin-top: 2px; font-size: 12px; color: var(--ai-muted); }
.ai-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #10b981; }
.ai-status.busy .ai-status-dot { background: var(--ai-primary); animation: ai-blink 1s ease-in-out infinite; }
.ai-header-actions { display: flex; gap: 2px; }
.ai-icon-btn { width: 34px; height: 34px; border: 0; border-radius: 10px; background: transparent; color: var(--ai-muted); font-size: 14px; cursor: pointer; transition: background-color 140ms ease, color 140ms ease; }
.ai-icon-btn:hover { background: var(--ai-soft); color: var(--ai-ink); }

.ai-messages { flex: 1; overflow-y: auto; padding: 18px 14px 6px; scroll-behavior: smooth; scrollbar-width: thin; scrollbar-color: var(--ai-line-strong) transparent; }

.ai-typing { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
.ai-typing-avatar { flex: 0 0 28px; height: 28px; border: 1px solid var(--ai-line); border-radius: 50%; background: var(--ai-surface); display: grid; place-items: center; }
.ai-typing-avatar img { width: 18px; height: 18px; object-fit: contain; }
.ai-typing-bubble { display: inline-flex; gap: 4px; padding: 12px 14px; border: 1px solid var(--ai-line); border-radius: 4px 14px 14px 14px; background: var(--ai-surface); }
.ai-typing-bubble span { width: 6px; height: 6px; border-radius: 50%; background: var(--ai-primary); opacity: 0.35; animation: ai-bounce 1s ease-in-out infinite; }
.ai-typing-bubble span:nth-child(2) { animation-delay: 140ms; }
.ai-typing-bubble span:nth-child(3) { animation-delay: 280ms; }

.ai-jump { position: absolute; left: 50%; bottom: 150px; transform: translateX(-50%); display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border: 1px solid var(--ai-line); border-radius: 999px; background: var(--ai-surface); color: var(--ai-primary); font-size: 12px; font-weight: 600; box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12); cursor: pointer; z-index: 2; }

.ai-drop { position: absolute; inset: 8px; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border: 2px dashed var(--ai-primary); border-radius: 14px; background: rgba(238, 242, 255, 0.94); color: var(--ai-primary); pointer-events: none; }
.ai-drop i { font-size: 28px; }
.ai-drop span { font-size: 12px; color: var(--ai-muted); }

.ai-pop-enter-active, .ai-pop-leave-active { transition: opacity 180ms ease, transform 220ms var(--ai-ease); }
.ai-pop-enter-from, .ai-pop-leave-to { opacity: 0; transform: translateY(10px) scale(0.96); }

@keyframes ai-bounce { 50% { opacity: 1; transform: translateY(-3px); } }
@keyframes ai-blink { 50% { opacity: 0.3; } }

@media (prefers-reduced-motion: reduce) {
  .ai-pop-enter-active, .ai-pop-leave-active, .ai-panel, .ai-fab { transition: none; }
  .ai-typing-bubble span, .ai-status.busy .ai-status-dot { animation: none; opacity: 0.7; }
  .ai-messages { scroll-behavior: auto; }
}

@media (max-width: 767px) {
  .ai-shell { right: 16px; bottom: calc(92px + env(safe-area-inset-bottom)); }
  .ai-shell.open { inset: 0; }
  .ai-shell.open .ai-panel, .ai-shell.open.expanded .ai-panel { width: 100vw; height: 100dvh; border: 0; border-radius: 0; }
  .ai-expand-btn { display: none; }
}
</style>
